/**
 * A PRODUCT HAS A READABLE ADDRESS, NOT A BARE UUID.
 *
 * Measured against the anchor merchant's own site on 2026-10-20, as Googlebot:
 *
 *   theirs  /product/שעון-נשים-daniel-klein-dk-1-13873-5/
 *   ours    /dk-store/p/b1579325-2e57-44d6-a2b5-e8b358500d85
 *
 * Their address says what the page sells in the words a Hebrew shopper types;
 * ours says nothing at all. The URL is read by the ranking system and shown in
 * the result, so a catalogue of 527 products was published under 527 strings
 * carrying zero query terms between them.
 *
 * ── WHY THIS IS NOT A STORED `slug` COLUMN ────────────────────────────────
 *
 * `ProductCategory.slug`, `ProductBrand.slug` and `ProductCollection.slug` are
 * stored, minted once and never re-derived, because for those three the slug
 * IS the address: `/shop/c/<slug>` has nothing else to resolve by, so two
 * shelves must never collide and a rename must never move a ranked URL.
 *
 * A product's address keeps its id — `/p/<slug>-<uuid>` — so none of that
 * applies. The uuid resolves the page and the slug is a readable prefix in
 * front of it. That buys three things a stored column would not:
 *
 *   - No collision rule. Two products may share a name; they cannot share an
 *     address, because the address ends in the id.
 *   - No migration and no backfill. Every product already has the only
 *     identifier this needs.
 *   - A rename is free. `/p/<old-name>-<uuid>` still resolves after a rename —
 *     the id is intact — and the server answers it with a 301 to the current
 *     spelling. No link anyone has shared can break, which is the exact
 *     guarantee the stored-slug rule exists to provide.
 *
 * ── AND IT KEEPS THE LETTERS ──────────────────────────────────────────────
 *
 * `slugFromName` (the server's one map, behind every stored slug) TRANSLITERATES:
 * `שעונים` becomes `shavnym`. That is right for an opaque stored identifier and
 * wrong here, because this half of the address is the part a person reads and a
 * search engine matches, and `shavnym` is a word in no language. `categorySlug.js`
 * settled this for category pages already, citing these same competitor URLs,
 * and this file is deliberately that file's rule applied to a product name:
 * strip what cannot sit in a path segment, keep every letter in every script,
 * and let the wire percent-encode it.
 */

/** An apostrophe inside a word is DELETED rather than turned into a separator —
 *  "Women's" is one word, and `women-s-watch` is not an address anyone types. */
const IN_WORD_PUNCTUATION = /['’ʼ]/g;

/** Characters that cannot sit in a path segment, plus the ones that would make
 *  a URL ambiguous to read or to copy out of a chat. Kept character-for-character
 *  in step with `categorySlug.js`; see the note there on why it is an explicit
 *  set and not the range `[ -/]`. */
const UNSAFE = /[\s\\/?#[\]@!$&'()*+,;=:"<>{}|^`~%]/g;

/** A v4 uuid as Prisma's `@default(uuid())` mints it, anchored to the END of a
 *  path segment — the id is always last, so the slug in front of it may contain
 *  anything at all, hyphens included, without making the address ambiguous. */
const TRAILING_UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** How much of a name goes in front of the id. Long enough for the words that
 *  matter, short enough that the whole path stays readable in a SERP. */
const MAX_SLUG_LENGTH = 70;

/** What an id may look like when it is not a uuid — every legacy id this
 *  platform has minted. Anchored, so one stray character refuses the whole
 *  segment rather than letting part of it through. */
const ID_SHAPED = /^[A-Za-z0-9_-]+$/;

/**
 * The readable half of a product's address. Empty when the name has nothing
 * that can live in a URL, so callers fall back to the bare id rather than
 * minting a leading hyphen.
 */
/* THE NAME, WHEREVER THIS ROW KEEPS IT. The strips that link to a product do
 * not hold a product object: `BuyAgainStrip` has `item.name`,
 * `LikedProductsStrip` has `item.productName`, and the shop grid has a real
 * product. One lookup here beats eight call sites each remembering which. A
 * row with none of them slugifies to "" and the address falls back to the bare
 * id — the URL this storefront minted before — so a field this misses costs
 * the words and never the link. */
export function productSlug(product) {
  // A bare string is a name; an object without one is not a product, and
  // `String({})` would otherwise slugify to `object-object`.
  // `addressName` first: a product put into the reader's language (utils/shopperText `localizedProduct`) keeps the
  // shop's own name there, so a Hebrew reader and an Arabic one are handed the same address.
  const raw = typeof product === 'string'
    ? product
    : (product?.addressName ?? product?.name ?? product?.productName ?? product?.title);
  const name = String(raw ?? '').trim();
  if (!name) return '';
  return name
    .normalize('NFC')
    .replace(IN_WORD_PUNCTUATION, '')
    .replace(UNSAFE, ' ')
    .replace(/[\s._]+/g, '-')
    .replace(/-{2,}/g, '-')
    // Cap BEFORE trimming hyphens, never after: a cut that lands on a separator
    // would otherwise leave one dangling against the id.
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

/**
 * The canonical path for one product. `basePath` is `''` on a custom domain and
 * `/<salonSlug>` on the platform, exactly as `getBasePath` returns it.
 *
 * Takes the id separately from the product so the strip components — which hold
 * a `productId` and a `name` on a flat row rather than a product object — can
 * call it without being rewritten.
 */
export function productPath(basePath, product, productId) {
  const id = String(productId ?? product?.id ?? product?.productId ?? '').trim();
  if (!id) return `${basePath}/shop`;
  const slug = productSlug(product);
  const segment = slug ? `${slug}-${id}` : id;
  return `${basePath}/p/${encodeURIComponent(segment)}`;
}

/**
 * Resolve an address back to the product it names.
 *
 * TAKES AN ALREADY-DECODED SEGMENT, and decodes nothing itself. The two
 * callers disagree about what they hold and an internal decode cannot be right
 * for both: the browser gets its value from `useParams()`, which react-router
 * has ALREADY percent-decoded, while the Node server splits a raw pathname and
 * holds the escaped bytes. Decoding here meant the client decoded twice, so
 * `%252e%252e%252f` arrived as `../` — and `storeApi` interpolates a product
 * id into an API path without escaping it. The server now decodes its segment
 * before calling (`matchShopEntityRoute`); the browser passes its param
 * through.
 *
 * AND THE ANSWER IS VALIDATED, not merely parsed. A trailing uuid wins; failing
 * that the whole segment must look like an id — letters, digits, `-` and `_` —
 * which is every id this platform has ever minted, including the legacy
 * non-uuid ones. Anything else is not an id and comes back empty, so a crafted
 * address cannot reach a URL builder at all, whatever any caller does with it.
 */
export function productIdFromParam(param) {
  const decoded = String(param ?? '').trim();
  if (!decoded) return '';
  const match = TRAILING_UUID.exec(decoded);
  if (match) return match[0];
  return ID_SHAPED.test(decoded) ? decoded : '';
}

/**
 * Whether the address a visitor arrived at is the canonical one for this
 * product. The server answers a non-canonical form with a 301; the client uses
 * this to correct the address bar after an in-app navigation without one.
 */
export function isCanonicalProductParam(param, product, productId) {
  const id = String(productId ?? product?.id ?? '').trim();
  if (!id) return true;
  // Already-decoded, like `productIdFromParam` above.
  const decoded = String(param ?? '');
  const slug = productSlug(product);
  return decoded === (slug ? `${slug}-${id}` : id);
}
